import React, { useState, useRef, useEffect } from "react"
import "./KYC.scss"
import {
    Button,
    Col,
    Modal,
    Row,
    Table,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Badge,
    Input,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { OverlayPanel } from "primereact/overlaypanel"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { useNavigate } from "react-router-dom"
import paymentApi from "Api/paymentApi"
import { StandardPagination, StatusBadge, ActionIcon, DocumentViewer } from "components/Common"

const KYC = () => {
    document.title = "KYC Verification | Pixwik - Admin"
    const navigate = useNavigate()
    const toast = useRef(null)
    const op = useRef(null)

    const [page, setPage] = useState(1)
    const [limit] = useState(10)
    const [loading, setLoading] = useState(false)
    const [modal, setModal] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [filterStatus, setFilterStatus] = useState("all")
    const [kycData, setKycData] = useState([])
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRequest: 0,
        hasNextPage: false,
        hasPrevPage: false
    })
    const [aadharRemarks, setAadharRemarks] = useState("")
    const [panRemarks, setPanRemarks] = useState("")
    const [passbookRemarks, setPassbookRemarks] = useState("")
    const [verifyingDocument, setVerifyingDocument] = useState(null)
    const [documentViewer, setDocumentViewer] = useState({
        isOpen: false,
        url: "",
        title: "",
        type: "image"
    })

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "KYC Verification", link: "#" },
    ]

    const handleBreadcrumbClick = link => {
        if (link !== "#") {
            navigate(link)
        }
    }

    // Fetch KYC data from API
    const fetchKycData = async () => {
        try {
            setLoading(true)
            const accessToken = localStorage.getItem("accessToken")
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                params: {
                    page,
                    limit,
                    ...(filterStatus !== "all" && { status: filterStatus })
                }
            }
            const response = await paymentApi.getKycDetails(config)

            if (response.data.status) {
                const apiData = response.data.data.popluatedkycRequest || []
                const transformedData = apiData.map(item => {
                    // Get document verification status from kycDocuments array
                    const aadharDoc = item.kycDocuments?.find(doc => doc.type === "Aadhar")
                    const panDoc = item.kycDocuments?.find(doc => doc.type === "PAN")
                    const passbookDoc = item.kycDocuments?.find(doc => doc.type === "Passbook")

                    return {
                        id: item._id,
                        userId: item.userId,
                        userName: item.user?.userName || item.user?.firstName + " " + item.user?.lastName || "N/A",
                        email: item.user?.email || "N/A",
                        phone: item.user?.phoneNumber || "N/A",
                        verificationStatus: item.kycStatus?.toLowerCase() || "pending",
                        submittedDate: item.createdAt,
                        kycLevel: item.meta?.kycLevel || "BASIC",
                        lastKycUpdate: item.meta?.lastKycUpdate,
                        aadharCard: {
                            number: item.aadharInfo?.aadharNumber || "N/A",
                            name: item.aadharInfo?.nameOnAadhar || "N/A",
                            dob: item.aadharInfo?.dateOfBirth || "N/A",
                            address: item.aadharInfo?.address ?
                                `${item.aadharInfo.address.house}, ${item.aadharInfo.address.street}, ${item.aadharInfo.address.vtc}, ${item.aadharInfo.address.dist}, ${item.aadharInfo.address.state}, ${item.aadharInfo.address.country} - ${item.aadharInfo.address.pincode}`
                                : "N/A",
                            verified: aadharDoc?.verified || false,
                            verifiedAt: aadharDoc?.verifiedAt,
                            remarks: aadharDoc?.remarks,
                            frontUrl: aadharDoc?.documentUrls?.front,
                            backUrl: aadharDoc?.documentUrls?.back
                        },
                        panCard: {
                            number: "N/A", // PAN number not in response
                            name: "N/A", // PAN name not in response
                            fatherName: "N/A", // Father name not in response
                            dob: "N/A", // PAN DOB not in response
                            verified: panDoc?.verified || false,
                            verifiedAt: panDoc?.verifiedAt,
                            remarks: panDoc?.remarks,
                            fileUrl: panDoc?.singleDocumentUrl
                        },
                        passbook: {
                            verified: passbookDoc?.verified || false,
                            verifiedAt: passbookDoc?.verifiedAt,
                            remarks: passbookDoc?.remarks,
                            fileUrl: passbookDoc?.singleDocumentUrl
                        },
                        kycDocuments: item.kycDocuments || []
                    }
                })

                setKycData(transformedData)
                
                // Use pagination data directly from API response
                const apiPagination = response.data.data.pagination
                
                console.log('KYC Pagination Debug:', {
                    apiPagination,
                    currentPage: page,
                    limit,
                    dataLength: transformedData.length
                })
                
                setPagination({
                    currentPage: apiPagination?.currentPage || page,
                    totalPages: apiPagination?.totalPages || 1,
                    totalRequest: apiPagination?.totalRequest || transformedData.length,
                    hasNextPage: apiPagination?.hasNextPage || false,
                    hasPrevPage: apiPagination?.hasPrevPage || false
                })

            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch KYC data",
                life: 3000,
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchKycData()
    }, [page, filterStatus])

    // Since we're doing server-side filtering, no need for client-side filtering
    const filteredKycData = kycData

    const toggle = () => {
        setModal(!modal)
        if (modal) {
            // Reset remarks when closing modal
            setAadharRemarks("")
            setPanRemarks("")
            setPassbookRemarks("")
        }
    }

    const handleViewKyc = (user) => {
        setSelectedUser(user)
        toggle()
    }

    const handleVerifyDocument = async (documentType) => {
        if (!selectedUser) return

        const remarks = documentType === "Aadhar" ? aadharRemarks :
            documentType === "PAN" ? panRemarks : passbookRemarks

        try {
            setVerifyingDocument(documentType)
            const accessToken = localStorage.getItem("accessToken")
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                }
            }
            const response = await paymentApi.verifyKycDocument(
                selectedUser.userId,
                documentType,
                remarks.trim() || undefined,
                config
            )

            if (response.data.status) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: `${documentType} verified successfully`,
                    life: 3000,
                })

                // Update local state
                const fieldName = documentType === "Aadhar" ? "aadharCard" :
                    documentType === "PAN" ? "panCard" : "passbook"

                setSelectedUser(prev => ({
                    ...prev,
                    [fieldName]: {
                        ...prev[fieldName],
                        verified: true
                    }
                }))

                // Refresh data
                await fetchKycData()

                // Clear remarks
                if (documentType === "Aadhar") {
                    setAadharRemarks("")
                } else if (documentType === "PAN") {
                    setPanRemarks("")
                } else {
                    setPassbookRemarks("")
                }
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Failed to verify document",
                life: 3000,
            })
        } finally {
            setVerifyingDocument(null)
        }
    }

    const handleOverallVerification = async () => {
        if (!selectedUser) return

        // Check if all required documents are verified
        const requiredDocuments = []
        if (!selectedUser.aadharCard.verified) requiredDocuments.push("Aadhar")
        if (!selectedUser.panCard.verified) requiredDocuments.push("PAN")
        if (selectedUser.passbook && selectedUser.passbook.fileUrl && !selectedUser.passbook.verified) {
            requiredDocuments.push("Passbook")
        }

        if (requiredDocuments.length > 0) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: `Please verify ${requiredDocuments.join(", ")} document${requiredDocuments.length > 1 ? 's' : ''} before approving KYC`,
                life: 3000,
            })
            return
        }

        try {
            const accessToken = localStorage.getItem("accessToken")
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                }
            }
            const response = await paymentApi.verifyKycStatus(selectedUser.userId, config)

            if (response.data.status) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "KYC verified successfully",
                    life: 3000,
                })

                // Refresh data
                await fetchKycData()
                toggle()
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Failed to verify KYC",
                life: 3000,
            })
        }
    }

    const handleViewDocument = (url, title, type = "image") => {
        setDocumentViewer({
            isOpen: true,
            url,
            title,
            type
        })
    }

    const closeDocumentViewer = () => {
        setDocumentViewer({
            isOpen: false,
            url: "",
            title: "",
            type: "image"
        })
    }

    const getFileTypeFromUrl = (url) => {
        if (!url) return 'image'
        const fileExtension = url.split('.').pop().toLowerCase()
        if (fileExtension === 'pdf') return 'pdf'
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileExtension)) return 'office'
        return 'image'
    }

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'warning'
            case 'verified':
            case 'approved':
                return 'success'
            case 'rejected':
                return 'danger'
            default:
                return 'secondary'
        }
    }

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'Pending'
            case 'verified':
            case 'approved':
                return 'Verified'
            case 'rejected':
                return 'Rejected'
            default:
                return status || 'Unknown'
        }
    }

    return (
        <>
            <Toast ref={toast} position="top-right" />
            <Row>
                <Col xs="12">
                    <div className="kyc-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="KYC Verification"
                            handleClick={handleBreadcrumbClick}
                        />
                    </div>



                    <div className="data-table-container">
                        <div className="table-responsive mt-4 mb-3">
                            {loading ? (
                                <DataTableLoader cols={6} />
                            ) : (
                                <Table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>S.No</th>
                                            <th>User Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>
                                                <span className="d-flex align-items-center gap-2 justify-content-center">
                                                    <span>Verification Status</span>
                                                    <span
                                                        onClick={e => op.current.toggle(e)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="mdi mdi-filter" />
                                                    </span>
                                                </span>
                                                <OverlayPanel ref={op}>
                                                    <Input
                                                        type="select"
                                                        value={filterStatus}
                                                        onChange={e => {
                                                            setFilterStatus(e.target.value)
                                                            setPage(1) // Reset to first page when filter changes
                                                            op.current.hide()
                                                        }}
                                                        style={{ width: "150px" }}
                                                    >
                                                        <option value="all">All</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="verified">Verified</option>
                                                        <option value="rejected">Rejected</option>
                                                    </Input>
                                                </OverlayPanel>
                                            </th>
                                            <th>Submitted Date</th>
                                            <th>View</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredKycData.length > 0 ? (
                                            filteredKycData.map((user, index) => (
                                                    <tr key={user.id} className="kyc-table-row">
                                                        <th scope="row">{(page - 1) * limit + index + 1}</th>
                                                        <td>{user.userName}</td>
                                                        <td>{user.email}</td>
                                                        <td>{user.phone}</td>
                                                        <td className="text-center">
                                                            <StatusBadge status={user.verificationStatus} />
                                                        </td>
                                                        <td>{new Date(user.submittedDate).toLocaleDateString()}</td>
                                                        <td>
                                                            <ActionIcon
                                                                type="view"
                                                                onClick={() => handleViewKyc(user)}
                                                                tooltip="View KYC"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    No KYC requests found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    </div>

                    {(pagination.totalPages > 1 || kycData.length > 0) && (
                        <div className="d-flex justify-content-end mt-3">
                            <StandardPagination
                                currentPage={page}
                                totalPages={Math.max(pagination.totalPages, 1)}
                                onPageChange={setPage}
                            />
                        </div>
                    )}
                </Col>
            </Row>

            {/* KYC Details Modal */}
            <Modal isOpen={modal} toggle={toggle} size="xl" centered>
                <ModalHeader toggle={toggle}>
                    KYC Verification Details - {selectedUser?.userName}
                </ModalHeader>
                <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
                    {selectedUser && (
                        <div className="kyc-details">
                            {/* User Basic Info */}
                            <div className="user-info mb-4">
                                <h5 className="mb-3">User Information</h5>
                                <Row>
                                    <Col md={6}>
                                        <p><strong>Name:</strong> {selectedUser.userName}</p>
                                        <p><strong>Email:</strong> {selectedUser.email}</p>
                                    </Col>
                                    <Col md={6}>
                                        <p><strong>Phone:</strong> {selectedUser.phone}</p>
                                        <p><strong>Status:</strong>
                                            <Badge color={getStatusBadge(selectedUser.verificationStatus)} className="ms-2">
                                                {getStatusText(selectedUser.verificationStatus)}
                                            </Badge>
                                        </p>
                                    </Col>
                                </Row>
                            </div>

                            {/* Aadhar Card Details */}
                            <div className="document-section mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>Aadhar Card Details</h5>
                                    <Button
                                        color={selectedUser.aadharCard.verified ? "success" : "primary"}
                                        size="sm"
                                        onClick={() => handleVerifyDocument("Aadhar")}
                                        disabled={selectedUser.aadharCard.verified || verifyingDocument === "Aadhar"}
                                    >
                                        {verifyingDocument === "Aadhar" ? (
                                            <>
                                                <i className="mdi mdi-loading mdi-spin me-1"></i>
                                                Verifying...
                                            </>
                                        ) : selectedUser.aadharCard.verified ? (
                                            <>
                                                <i className="mdi mdi-check me-1"></i>
                                                Verified
                                            </>
                                        ) : (
                                            <>
                                                <i className="mdi mdi-check-circle me-1"></i>
                                                Verify
                                            </>
                                        )}
                                    </Button>
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <p><strong>Aadhar Number:</strong> {selectedUser.aadharCard.number}</p>
                                        <p><strong>Name:</strong> {selectedUser.aadharCard.name}</p>
                                        <p><strong>Date of Birth:</strong> {
                                            selectedUser.aadharCard.dob !== "N/A" ?
                                                new Date(selectedUser.aadharCard.dob).toLocaleDateString() :
                                                "N/A"
                                        }</p>
                                        {selectedUser.aadharCard.verifiedAt && (
                                            <p><strong>Verified At:</strong> {new Date(selectedUser.aadharCard.verifiedAt).toLocaleDateString()}</p>
                                        )}
                                        {selectedUser.aadharCard.remarks && (
                                            <p><strong>Remarks:</strong> {selectedUser.aadharCard.remarks}</p>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <p><strong>Address:</strong> {selectedUser.aadharCard.address}</p>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {selectedUser.aadharCard.frontUrl && (
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => handleViewDocument(
                                                        selectedUser.aadharCard.frontUrl,
                                                        `${selectedUser.userName} - Aadhar Front`,
                                                        getFileTypeFromUrl(selectedUser.aadharCard.frontUrl)
                                                    )}
                                                >
                                                    <i className="mdi mdi-eye me-1"></i>
                                                    View Front
                                                </Button>
                                            )}
                                            {selectedUser.aadharCard.backUrl && (
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => handleViewDocument(
                                                        selectedUser.aadharCard.backUrl,
                                                        `${selectedUser.userName} - Aadhar Back`,
                                                        getFileTypeFromUrl(selectedUser.aadharCard.backUrl)
                                                    )}
                                                >
                                                    <i className="mdi mdi-eye me-1"></i>
                                                    View Back
                                                </Button>
                                            )}

                                        </div>
                                    </Col>
                                </Row>
                                {!selectedUser.aadharCard.verified && (
                                    <Row className="mt-3">
                                        <Col md={12}>
                                            <Input
                                                type="textarea"
                                                placeholder="Enter remarks for Aadhar verification"
                                                value={aadharRemarks}
                                                onChange={(e) => setAadharRemarks(e.target.value)}
                                                rows={3}
                                                disabled={selectedUser.aadharCard.verified}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </div>

                            {/* PAN Card Details */}
                            <div className="document-section mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5>PAN Card Details</h5>
                                    <Button
                                        color={selectedUser.panCard.verified ? "success" : "primary"}
                                        size="sm"
                                        onClick={() => handleVerifyDocument("PAN")}
                                        disabled={selectedUser.panCard.verified || verifyingDocument === "PAN"}
                                    >
                                        {verifyingDocument === "PAN" ? (
                                            <>
                                                <i className="mdi mdi-loading mdi-spin me-1"></i>
                                                Verifying...
                                            </>
                                        ) : selectedUser.panCard.verified ? (
                                            <>
                                                <i className="mdi mdi-check me-1"></i>
                                                Verified
                                            </>
                                        ) : (
                                            <>
                                                <i className="mdi mdi-check-circle me-1"></i>
                                                Verify
                                            </>
                                        )}
                                    </Button>
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <p><strong>PAN Number:</strong> {selectedUser.panCard.number}</p>
                                        <p><strong>Name:</strong> {selectedUser.panCard.name}</p>
                                        <p><strong>Father's Name:</strong> {selectedUser.panCard.fatherName}</p>
                                    </Col>
                                    <Col md={6}>
                                        <p><strong>Date of Birth:</strong> {selectedUser.panCard.dob}</p>
                                        {selectedUser.panCard.verifiedAt && (
                                            <p><strong>Verified At:</strong> {new Date(selectedUser.panCard.verifiedAt).toLocaleDateString()}</p>
                                        )}
                                        {selectedUser.panCard.remarks && (
                                            <p><strong>Remarks:</strong> {selectedUser.panCard.remarks}</p>
                                        )}
                                        <p><strong>Document:</strong>
                                            {selectedUser.panCard.fileUrl ? (
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    className="ms-2"
                                                    onClick={() => handleViewDocument(
                                                        selectedUser.panCard.fileUrl,
                                                        `${selectedUser.userName} - PAN Card`,
                                                        getFileTypeFromUrl(selectedUser.panCard.fileUrl)
                                                    )}
                                                >
                                                    <i className="mdi mdi-eye me-1"></i>
                                                    View PAN
                                                </Button>
                                            ) : (
                                                <span className="ms-2 text-muted">No document available</span>
                                            )}
                                        </p>

                                    </Col>
                                </Row>
                                {!selectedUser.panCard.verified && (
                                    <Row className="mt-3">
                                        <Col md={12}>
                                            <Input
                                                type="textarea"
                                                placeholder="Enter remarks for PAN verification"
                                                value={panRemarks}
                                                onChange={(e) => setPanRemarks(e.target.value)}
                                                rows={3}
                                                disabled={selectedUser.panCard.verified}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </div>

                            {/* Passbook Details */}
                            {selectedUser.passbook && selectedUser.passbook.fileUrl && (
                                <div className="document-section mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>Passbook Details</h5>
                                        <Button
                                            color={selectedUser.passbook.verified ? "success" : "primary"}
                                            size="sm"
                                            onClick={() => handleVerifyDocument("Passbook")}
                                            disabled={selectedUser.passbook.verified || verifyingDocument === "Passbook"}
                                        >
                                            {verifyingDocument === "Passbook" ? (
                                                <>
                                                    <i className="mdi mdi-loading mdi-spin me-1"></i>
                                                    Verifying...
                                                </>
                                            ) : selectedUser.passbook.verified ? (
                                                <>
                                                    <i className="mdi mdi-check me-1"></i>
                                                    Verified
                                                </>
                                            ) : (
                                                <>
                                                    <i className="mdi mdi-check-circle me-1"></i>
                                                    Verify
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                    <Row>
                                        <Col md={6}>
                                            {selectedUser.passbook.verifiedAt && (
                                                <p><strong>Verified At:</strong> {new Date(selectedUser.passbook.verifiedAt).toLocaleDateString()}</p>
                                            )}
                                            {selectedUser.passbook.remarks && (
                                                <p><strong>Remarks:</strong> {selectedUser.passbook.remarks}</p>
                                            )}
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Document:</strong>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    className="ms-2"
                                                    onClick={() => handleViewDocument(
                                                        selectedUser.passbook.fileUrl,
                                                        `${selectedUser.userName} - Passbook`,
                                                        getFileTypeFromUrl(selectedUser.passbook.fileUrl)
                                                    )}
                                                >
                                                    <i className="mdi mdi-eye me-1"></i>
                                                    View Passbook
                                                </Button>
                                            </p>
                                        </Col>
                                    </Row>
                                    {!selectedUser.passbook.verified && (
                                        <Row className="mt-3">
                                            <Col md={12}>
                                                <Input
                                                    type="textarea"
                                                    placeholder="Enter remarks for Passbook verification"
                                                    value={passbookRemarks}
                                                    onChange={(e) => setPassbookRemarks(e.target.value)}
                                                    rows={3}
                                                    disabled={selectedUser.passbook.verified}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                </div>
                            )}

                            {/* Additional KYC Info */}
                            <div className="document-section mb-4">
                                <h5 className="mb-3">Additional Information</h5>
                                <Row>
                                    <Col md={6}>
                                        <p><strong>KYC Level:</strong> {selectedUser.kycLevel}</p>
                                        <p><strong>Submitted Date:</strong> {new Date(selectedUser.submittedDate).toLocaleDateString()}</p>
                                    </Col>
                                    <Col md={6}>
                                        {selectedUser.lastKycUpdate && (
                                            <p><strong>Last Update:</strong> {new Date(selectedUser.lastKycUpdate).toLocaleDateString()}</p>
                                        )}
                                        <p><strong>Total Documents:</strong> {selectedUser.kycDocuments.length}</p>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex gap-2 w-100 justify-content-between">
                        <Button color="secondary" onClick={toggle}>
                            Close
                        </Button>
                        <div className="d-flex gap-2">
                            <Button
                                color="success"
                                onClick={handleOverallVerification}
                                disabled={
                                    selectedUser?.verificationStatus === "verified" ||
                                    !selectedUser?.aadharCard.verified ||
                                    !selectedUser?.panCard.verified ||
                                    (selectedUser?.passbook?.fileUrl && !selectedUser?.passbook?.verified)
                                }
                            >
                                <i className="mdi mdi-check me-1"></i>
                                {selectedUser?.verificationStatus === "verified" ? "Already Verified" : "Verify KYC"}
                            </Button>
                        </div>
                    </div>
                    {selectedUser && (() => {
                        const unverifiedDocs = []
                        if (!selectedUser.aadharCard.verified) unverifiedDocs.push("Aadhar")
                        if (!selectedUser.panCard.verified) unverifiedDocs.push("PAN")
                        if (selectedUser.passbook?.fileUrl && !selectedUser.passbook.verified) unverifiedDocs.push("Passbook")

                        if (unverifiedDocs.length > 0) {
                            return (
                                <div className="w-100 text-center mt-2">
                                    <small className="text-muted">
                                        Please verify {unverifiedDocs.join(", ")} document{unverifiedDocs.length > 1 ? 's' : ''} to enable KYC verification
                                    </small>
                                </div>
                            )
                        }
                        return null
                    })()}
                </ModalFooter>
            </Modal>

            {/* Document Viewer Modal */}
            <DocumentViewer
                isOpen={documentViewer.isOpen}
                toggle={closeDocumentViewer}
                documentUrl={documentViewer.url}
                documentTitle={documentViewer.title}
                documentType={documentViewer.type}
            />
        </>
    )
}

export default KYC